import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FIELDS = ["name", "date", "end_date", "location", "weapon", "category", "level", "competition_type", "technical_priority", "planning_status", "notes"];
const NONE = "__none";
const PLANNING = ["Confermata", "Programmabile", "Provvisoria", "Da confermare"];

// Modifica gara esistente: stesso ID, PUT del documento completo (convocati/risultati preservati)
export const CompetitionEditDialog = ({ competition, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !competition) return;
    const f = {};
    FIELDS.forEach((k) => { f[k] = competition[k] || ""; });
    f.date = (competition.date || "").slice(0, 10);
    f.end_date = (competition.end_date || "").slice(0, 10);
    setForm(f);
  }, [open, competition]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const sel = (k, v) => set(k, v === NONE ? "" : v);

  const save = async () => {
    if (!form.name?.trim() || !form.date) { toast.error("Denominazione e data sono obbligatorie"); return; }
    setSaving(true);
    try {
      const { data: current } = await api.get(`/competitions/${competition.id}`);
      const patch = {};
      FIELDS.forEach((k) => { patch[k] = form[k] === "" ? null : form[k]; });
      await api.put(`/competitions/${competition.id}`, { ...current, ...patch, name: form.name.trim() });
      toast.success("Gara aggiornata");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio gara"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="competition-edit-dialog">
        <DialogHeader><DialogTitle>Modifica gara</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Denominazione</Label><Input data-testid="comp-edit-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data</Label><Input data-testid="comp-edit-date" type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label>Data fine (opzionale)</Label><Input data-testid="comp-edit-end-date" type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Sede / Città</Label><Input data-testid="comp-edit-location" value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
            <div>
              <Label>Arma</Label>
              <Select value={form.weapon || NONE} onValueChange={(v) => sel("weapon", v)}>
                <SelectTrigger data-testid="comp-edit-weapon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  <SelectItem value="fioretto">Fioretto</SelectItem>
                  <SelectItem value="spada">Spada</SelectItem>
                  <SelectItem value="sciabola">Sciabola</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Categoria / Settore</Label><Input data-testid="comp-edit-category" value={form.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="es. ASS, CAD, NV, PARA" /></div>
            <div><Label>Livello</Label><Input data-testid="comp-edit-level" value={form.level || ""} onChange={(e) => set("level", e.target.value)} placeholder="REG / NAT / INT" /></div>
            <div><Label>Tipologia</Label><Input data-testid="comp-edit-type" value={form.competition_type || ""} onChange={(e) => set("competition_type", e.target.value)} placeholder="es. Qualificazione" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priorità tecnica</Label>
              <Select value={form.technical_priority || NONE} onValueChange={(v) => sel("technical_priority", v)}>
                <SelectTrigger data-testid="comp-edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {["A", "B", "C"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stato pianificazione</Label>
              <Select value={form.planning_status || NONE} onValueChange={(v) => sel("planning_status", v)}>
                <SelectTrigger data-testid="comp-edit-planning"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {PLANNING.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Note</Label><Textarea data-testid="comp-edit-notes" rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="comp-edit-cancel">Annulla</Button>
          <Button data-testid="comp-edit-save" onClick={save} disabled={saving} className="bg-black hover:bg-zinc-800">{saving ? "Salvataggio…" : "Salva modifiche"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
